import Button from 'antd/lib/button';
import Form, { RuleObject } from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import App from 'antd/lib/app';
import { PjmLocationDto } from 'src/domain/pjm/dto/PjmLocationDto';
import Input from 'antd/lib/input';
import Typography from 'antd/lib/typography';
import { Col, Row } from 'antd/lib/grid';
import Divider from 'antd/lib/divider';
import Checkbox from 'antd/lib/checkbox';
import TypedInputNumber from 'antd/lib/input-number';
import { EDC_DEP_MAP, EDC_VALUES, ZONE_DEP_MAP } from 'src/domain/pjm/values';
import Select from 'antd/lib/select';
import { PjmLoadReductionType, PjmLocationStatus } from 'src/domain/pjm/dto/enums';
import { StatusTag } from 'src/components/StatusTag';
import { PjmSyncStatusDetailed } from '../../../PjmSyncStatusDetailed';
import { usePjmLocationCreateMutation, usePjmLocationUpdateMutation } from 'src/domain/pjm/queries';
import { LocationRegistrationTable } from '../../components/LocationRegistrationTable';
import { BusinessSegmentSelector } from '../../components/BusinessSegmentSelector';
import { ResidentialProgramTypeSelector } from '../../components/ResidentialProgramTypeSelector';
import { LocationMeterInfoTable } from '../../components/LocationMeterInfoTable';
import InfoCircleFilled from '@ant-design/icons/lib/icons/InfoCircleFilled';
import Space from 'antd/lib/space';
import { LocationGeneratorInfoTable } from '../../components/LocationGeneratorInfoTable';
import { LocationBatteryInfoTable } from '../../components/LocationBatteryInfoTable';
import { useEffect, useState } from 'react';
import { InputNumber, Tooltip } from 'antd/lib';
import { PjmGeneratorInfo } from 'src/domain/pjm/dto/PjmGeneratorInfo';
import { PjmBatteryInfo } from 'src/domain/pjm/dto/PjmBatteryInfo';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import { PRIMARY_COLOR } from 'src/theme';
import { locationAddress, locationEdcAccountNumber, locationName, locationZipCode } from 'src/domain/pjm/validators';
import { NamePath, StoreValue } from 'antd/lib/form/interface';
import { RegisterButton } from './RegisterButton';
import { PjmLocationForm, PjmLocationTransformer } from './PjmLocationTransformer';
import { PjmLocationMeterInfo } from 'src/domain/pjm/dto/PjmLocationMeterInfo';
import './PjmLocationModal.css';

interface IPjmLocationModalProps {
    value?: PjmLocationDto;
    onClose: (actionTaken: boolean) => void;
}

export const PjmLocationModal = ({ value, onClose }: IPjmLocationModalProps) => {
    const { notification } = App.useApp();
    const [form] = Form.useForm<PjmLocationForm>();

    const [showGeneratorInfoTable, setShowGeneratorInfoTable] = useState(false);
    const [showBatteryInfoTable, setShowBatteryInfoTable] = useState(false);

    const location = PjmLocationTransformer.toFormValues(value!);
    const isNew = !location?.uuid;

    const utilityResidentialParticipant = Form.useWatch(['siteInfo', 'utilityResidentialParticipant'], form);
    const edcName = Form.useWatch(['summaryInfo', 'EDC', 'name'], form);
    const zone = Form.useWatch(['summaryInfo', 'zone'], form);
    const reductionInfoMap = Form.useWatch(['reductionInfoMap'], form);
    Form.useWatch(['meterInfo'], { form, preserve: true });

    useEffect(() => {
        const loadReduction = calculateLoadReduction(reductionInfoMap);
        form.setFieldValue(['profileInfo', 'loadReduction'], loadReduction);

        form.validateFields([
            ['reductionInfoMap', 'MANUFACTURING'],
            ['reductionInfoMap', 'HVAC'],
            ['reductionInfoMap', 'GENERATOR'],
            ['reductionInfoMap', 'BATTERIES'],
            ['reductionInfoMap', 'LIGHTING'],
            ['reductionInfoMap', 'WATER_HEATERS'],
            ['reductionInfoMap', 'REFRIGERATION'],
            ['reductionInfoMap', 'PLUG_LOAD'],
            ['reductionInfoMap', 'COMPUTING'],
            ['profileInfo', 'maxLoad'],
        ]);
    }, [reductionInfoMap]);

    function onMeterInfoChange(data: Array<PjmLocationMeterInfo>) {
        const records = data || [];
        form.setFieldValue('meterInfo', records);
    }

    /**
     * When generators info is changed,
     * we need to recalculate reduction info for generators and generatorCount
     */
    function onGeneratorInfoChange(data: Array<PjmGeneratorInfo & { _id: string }>) {
        const records = data || [];
        form.setFieldValue('generatorInfo', records);
        form.setFieldValue('generatorCount', records.length);

        const reductionInfoMap = form.getFieldValue('reductionInfoMap') || {};
        const maxOutput = records.reduce((acc, it) => acc + (it.maxOutput ?? 0), 0);
        reductionInfoMap[PjmLoadReductionType.GENERATOR] = maxOutput;
        form.setFieldValue('reductionInfoMap', reductionInfoMap);
    }

    function onBatteryInfoChange(data: Array<PjmBatteryInfo & { _id: string }>) {
        const records = data || [];
        form.setFieldValue('batteryInfo', records);

        const reductionInfoMap = form.getFieldValue('reductionInfoMap') || {};
        const maxOutput = records.reduce((acc, it) => acc + (it.maxOutput ?? 0), 0);
        reductionInfoMap[PjmLoadReductionType.BATTERIES] = maxOutput;
        form.setFieldValue('reductionInfoMap', reductionInfoMap);
    }

    /** available options for zone, pricingPoint and state depends on selected EDC name */
    const edcNameOptions = EDC_VALUES.map(it => ({ label: it, value: it }));
    let zoneOptions: { label: string; value: string }[] = [];
    let pricingPointOptions: { label: string; value: string }[] = [];
    let stateOptions: { label: string; value: string }[] = [];

    if (edcName) {
        const { zone, state } = EDC_DEP_MAP[edcName];
        zoneOptions = zone.map(it => ({ label: it, value: it }));
        stateOptions = state.map(it => ({ label: it, value: it }));
    }

    if (zone) {
        const { pricingPoint } = ZONE_DEP_MAP[zone];
        pricingPointOptions = pricingPoint.map(it => ({ label: it, value: it }));
    }

    function onEdcNameChange(val: string) {
        /** when EDC name is changed - we need to clear selected values for all dependent fields */
        form.setFieldValue(['summaryInfo', 'zone'], undefined);
        form.setFieldValue(['summaryInfo', 'pricingPoint'], undefined);
        form.setFieldValue(['siteInfo', 'address', 'state'], undefined);
        form.validateFields([
            ['summaryInfo', 'EDC', 'accountNo'],
            ['summaryInfo', 'zone'],
            ['summaryInfo', 'pricingPoint'],
            ['siteInfo', 'address', 'state'],
        ]);
    }

    function onZoneChange(val: string) {
        form.setFieldValue(['summaryInfo', 'pricingPoint'], undefined);
        form.validateFields([['summaryInfo', 'pricingPoint']]);
    }

    const pjmLocationCreateMutation = usePjmLocationCreateMutation();
    const pjmLocationUpdateMutation = usePjmLocationUpdateMutation();
    const isLoading = pjmLocationCreateMutation.isLoading || pjmLocationUpdateMutation.isLoading;

    async function onFormFinish(data: PjmLocationForm) {
        console.info(`PJM Location onFormFinish`, data);
        const dto = data;

        try {
            if (isNew) {
                const createDto = PjmLocationTransformer.toCreateDto(dto);
                // console.info('createDto: ', createDto);
                await pjmLocationCreateMutation.mutateAsync(createDto);
            } else {
                const updateDto = PjmLocationTransformer.toUpdateDto(dto);
                // console.info('updateDto: ', updateDto);
                await pjmLocationUpdateMutation.mutateAsync({ uuid: dto.uuid, dto: updateDto });
            }

            notification.success({ key: makeKey('save-notification'), message: 'PJM Location saved successfully!' });
            onClose(true);
        } catch (err: any) {
            notification.error({ key: makeKey('save-notification'), message: err?.message || 'Cannot save PJM Location!' });
        }
    }

    function onRegisterModalClose(actionTaken: boolean) {
        /** if registration was created - close Location modal too */
        if (actionTaken) {
            onClose(true);
        }
    }

    const isEdcNameDisabled =
        [PjmLocationStatus.ACTIVE, PjmLocationStatus.INACTIVE].includes(location.summaryInfo.status!) || Boolean(location.registrations?.length);

    const isZoneDisabled =
        [PjmLocationStatus.ACTIVE, PjmLocationStatus.INACTIVE].includes(location.summaryInfo.status!) || Boolean(location.registrations?.length);

    return (
        <div>
            <Modal
                title={value ? 'Edit PJM Location' : 'Create PJM Location'}
                className="pjm-location-modal"
                open
                destroyOnHidden
                maskClosable={false}
                keyboard={false}
                onCancel={() => onClose(false)}
                width="85%"
                footer={[
                    <Button key={makeKey('cancel')} disabled={isLoading} onClick={() => onClose(false)}>
                        Cancel
                    </Button>,
                    <RegisterButton onClose={onRegisterModalClose} form={form} location={location} />,
                    <Button key={makeKey('submit')} type="primary" disabled={isLoading} loading={isLoading} onClick={form.submit}>
                        Save
                    </Button>,
                ]}
            >
                <Form form={form} name="pjm-location-form" preserve={false} layout="vertical" onFinish={onFormFinish} initialValues={location}>
                    {!isNew && <PjmSyncStatusDetailed value={location!} />}

                    <div className="form-section">
                        <Row gutter={16} align="top">
                            <Col lg={12} xl={8}>
                                <Row gutter={8} align="top">
                                    <Col span={12}>
                                        <Form.Item hidden name="uuid">
                                            <Input disabled />
                                        </Form.Item>
                                        <Form.Item name="id" label={<Typography.Text strong>ID</Typography.Text>}>
                                            <Input disabled />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item name={['summaryInfo', 'status']} label={<Typography.Text strong>Status</Typography.Text>}>
                                            <StatusTag status={location.summaryInfo.status!} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={8} align="top">
                                    <Col span={24}>
                                        <Form.Item
                                            name={['summaryInfo', 'name']}
                                            label={<Typography.Text strong>Location</Typography.Text>}
                                            hasFeedback
                                            rules={[
                                                { required: true, message: 'Please enter location name!' },
                                                { max: 100, message: 'Number of characters should be less than 100' },
                                                { pattern: locationName.REGEX, message: locationName.errorMessage },
                                            ]}
                                        >
                                            <Input placeholder="Location name" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={8} align="top">
                                    <Col span={12}>
                                        <Form.Item
                                            name={['summaryInfo', 'CSP', 'name']}
                                            label={<Typography.Text strong>CSP</Typography.Text>}
                                            rules={[{ required: true, message: 'Please select CSP!' }]}
                                        >
                                            <Input disabled />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={8} align="top">
                                    <Col span={12}>
                                        <Form.Item
                                            name={['summaryInfo', 'EDC', 'name']}
                                            label={<Typography.Text strong>EDC</Typography.Text>}
                                            hasFeedback
                                            rules={[{ required: true, message: 'Please select EDC!' }]}
                                        >
                                            <Select
                                                showSearch
                                                autoClearSearchValue
                                                optionFilterProp="label"
                                                placeholder="Select EDC"
                                                options={edcNameOptions}
                                                onChange={onEdcNameChange}
                                                disabled={isEdcNameDisabled}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name={['summaryInfo', 'EDC', 'accountNo']}
                                            label={<Typography.Text strong>EDC Account Number</Typography.Text>}
                                            hasFeedback
                                            rules={[
                                                { required: true, message: 'Please enter EDC account number!' },
                                                createEdcAccountNumberValidator(edcName),
                                            ]}
                                        >
                                            <Input placeholder="EDC Account Number" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={8} align="top">
                                    <Col span={12}>
                                        <Form.Item
                                            name={['summaryInfo', 'zone']}
                                            label={<Typography.Text strong>Zone</Typography.Text>}
                                            hasFeedback
                                            rules={[{ required: true, message: 'Please select Zone!' }]}
                                        >
                                            <Select
                                                showSearch
                                                autoClearSearchValue
                                                optionFilterProp="label"
                                                placeholder="Select Zone"
                                                options={zoneOptions}
                                                onChange={onZoneChange}
                                                disabled={isZoneDisabled}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name={['summaryInfo', 'pricingPoint']}
                                            label={<Typography.Text strong>Pricing Point</Typography.Text>}
                                        >
                                            <Select
                                                showSearch
                                                autoClearSearchValue
                                                allowClear
                                                optionFilterProp="label"
                                                placeholder="Select Pricing Point"
                                                options={pricingPointOptions}
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Row gutter={8} align="top">
                                    <Col span={12}>
                                        <Form.Item
                                            name={['summaryInfo', 'allowEDCtoSubmitData']}
                                            label={<Typography.Text strong>Allow EDC to submit data</Typography.Text>}
                                            valuePropName="checked"
                                        >
                                            <Checkbox></Checkbox>
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name={['summaryInfo', 'allowLSEtoSubmitData']}
                                            label={<Typography.Text strong>Allow LSE to submit data</Typography.Text>}
                                            valuePropName="checked"
                                        >
                                            <Checkbox></Checkbox>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Col>
                            <Col lg={12} xl={8}>
                                <Form.Item
                                    name={['siteInfo', 'businessSegment']}
                                    label={<Typography.Text strong>Business Segment</Typography.Text>}
                                    hasFeedback
                                    rules={[{ required: true, message: 'Please select Business Segment!' }]}
                                >
                                    <BusinessSegmentSelector />
                                </Form.Item>
                                <Form.Item
                                    name={['siteInfo', 'address', 'address1']}
                                    label={<Typography.Text strong>Address1</Typography.Text>}
                                    rules={[
                                        { required: true, message: 'Please enter address!' },
                                        { max: 80, message: 'Number of characters should be less than 80' },
                                        { pattern: locationAddress.REGEX, message: locationAddress.errorMessage },
                                    ]}
                                >
                                    <Input placeholder="Address" />
                                </Form.Item>
                                <Form.Item
                                    name={['siteInfo', 'address', 'address2']}
                                    label={<Typography.Text strong>Address2</Typography.Text>}
                                    rules={[
                                        { max: 80, message: 'Number of characters should be less than 80' },
                                        { pattern: locationAddress.REGEX, message: locationAddress.errorMessage },
                                    ]}
                                >
                                    <Input placeholder="Address additional details" />
                                </Form.Item>
                                <Form.Item
                                    name={['siteInfo', 'address', 'city']}
                                    label={<Typography.Text strong>City</Typography.Text>}
                                    rules={[
                                        { required: true, message: 'Please enter city!' },
                                        { max: 40, message: 'Number of characters should be less than 40' },
                                        { pattern: locationAddress.REGEX, message: locationAddress.errorMessage },
                                    ]}
                                >
                                    <Input placeholder="City" />
                                </Form.Item>
                                <Row gutter={8} align="bottom">
                                    <Col span={12}>
                                        <Form.Item
                                            name={['siteInfo', 'address', 'state']}
                                            label={<Typography.Text strong>State</Typography.Text>}
                                            hasFeedback
                                            rules={[{ required: true, message: 'Please select state!' }]}
                                        >
                                            <Select
                                                showSearch
                                                autoClearSearchValue
                                                optionFilterProp="label"
                                                placeholder="Select state"
                                                options={stateOptions}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col span={12}>
                                        <Form.Item
                                            name={['siteInfo', 'address', 'zipCode']}
                                            label={<Typography.Text strong>Zip Code</Typography.Text>}
                                            rules={[
                                                { required: true, message: 'Please enter Zip code!' },
                                                { min: 5, max: 10, message: 'Number of characters should be between 5 and 10 digits and "-"' },
                                                { pattern: locationZipCode.REGEX, message: locationZipCode.errorMessage },
                                            ]}
                                        >
                                            <Input placeholder="Zip code" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Form.Item
                                    name={['siteInfo', 'utilityResidentialParticipant']}
                                    label={<Typography.Text strong>Utility Residential Participation?</Typography.Text>}
                                    valuePropName="checked"
                                >
                                    <Checkbox></Checkbox>
                                </Form.Item>
                                {utilityResidentialParticipant && (
                                    <Row gutter={8} align="bottom">
                                        <Col span={12}>
                                            <Form.Item
                                                name={['siteInfo', 'participantCount']}
                                                label={<Typography.Text strong># Of Participants</Typography.Text>}
                                                rules={[{ required: true }, createUtilityResidentialCountValidator]}
                                            >
                                                <TypedInputNumber />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item
                                                name={['siteInfo', 'residentialProgramType']}
                                                label={<Typography.Text strong>Type</Typography.Text>}
                                                rules={[{ required: true }, createUtilityResidentialProgramTypeValidator]}
                                            >
                                                <ResidentialProgramTypeSelector />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                            <Col lg={0} xl={8}></Col>
                        </Row>
                    </div>

                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Meter Equipment
                        </Divider>

                        <Form.Item name={['meterInfo']} noStyle>
                            <LocationMeterInfoTable value={form.getFieldValue('meterInfo')} onChange={onMeterInfoChange} />
                        </Form.Item>
                    </div>

                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Registration Input
                        </Divider>
                        <Row gutter={8} align="bottom">
                            <Col span={8}>
                                <Form.Item
                                    name={['profileInfo', 'peakLoadContribution']}
                                    label={<Typography.Text strong>Peak Load Contribution (kW)</Typography.Text>}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={9999999.999} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    name={['profileInfo', 'winterPeakLoad']}
                                    label={<Typography.Text strong>Winter Peak Load (kW)</Typography.Text>}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={9999999.999} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    name={['profileInfo', 'capacityLossFactor']}
                                    label={<Typography.Text strong>Capacity Loss Factor</Typography.Text>}
                                >
                                    <InputNumber style={{ width: '100px' }} step={0.1} min={0} max={1.99999} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8} align="bottom">
                            <Col span={8}>
                                <Form.Item
                                    name={['profileInfo', 'energyLossFactor']}
                                    label={<Typography.Text strong>Energy Loss Factor</Typography.Text>}
                                >
                                    <InputNumber style={{ width: '100px' }} step={0.1} min={0} max={1.99999} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    name={['profileInfo', 'maxLoad']}
                                    label={<Typography.Text strong>Max Load (kW)</Typography.Text>}
                                    rules={[createMaxLoadValidator]}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={9999999.999} />
                                </Form.Item>
                            </Col>

                            <Col span={8}>
                                <Form.Item
                                    name={['profileInfo', 'loadReduction']}
                                    label={
                                        <Typography.Text strong>
                                            Maximum Load Reduction Capability (kW)
                                            <Tooltip title="Maximum Load Reduction Capability (kW) is calculated automatically after adding Load Reduction method">
                                                <InfoCircleOutlined style={{ color: PRIMARY_COLOR, fontSize: '16px', marginLeft: '8px' }} />
                                            </Tooltip>
                                        </Typography.Text>
                                    }
                                >
                                    <InputNumber disabled style={{ width: '100px' }} />
                                </Form.Item>
                            </Col>
                        </Row>
                    </div>

                    <div className="form-section">
                        <Divider orientation="left" orientationMargin="0">
                            Load Reduction Method
                        </Divider>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <Form.Item
                                    name={['reductionInfoMap', 'MANUFACTURING']}
                                    label={<Typography.Text strong>Manufacturing (kW)</Typography.Text>}
                                    rules={[createReductionInfoMethodValidator]}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={999999.999} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    name={['reductionInfoMap', 'HVAC']}
                                    label={<Typography.Text strong>HVAC (kW)</Typography.Text>}
                                    rules={[createReductionInfoMethodValidator]}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={999999.999} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item label={<Typography.Text strong>Generator (kW)</Typography.Text>}>
                                    <Form.Item noStyle name={['reductionInfoMap', 'GENERATOR']} rules={[createReductionInfoMethodValidator]}>
                                        <InputNumber style={{ width: '100px' }} disabled />
                                    </Form.Item>
                                    {!showGeneratorInfoTable && (
                                        <Tooltip title="Show generators info">
                                            <Button
                                                icon={<InfoCircleFilled />}
                                                type="text"
                                                onClick={() => setShowGeneratorInfoTable(true)}
                                                style={{ fontSize: '22px', color: 'rgb(68 68 68 / 60%)' }}
                                            ></Button>
                                        </Tooltip>
                                    )}
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item name={['generatorCount']} label={<Typography.Text strong>Number of Generators</Typography.Text>}>
                                    <InputNumber style={{ width: '100px' }} disabled />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item label={<Typography.Text strong>Batteries (kW)</Typography.Text>}>
                                    <Form.Item noStyle name={['reductionInfoMap', 'BATTERIES']} rules={[createReductionInfoMethodValidator]}>
                                        <InputNumber style={{ width: '100px' }} disabled />
                                    </Form.Item>
                                    {!showBatteryInfoTable && (
                                        <Tooltip title="Show batteries info">
                                            <Button
                                                icon={<InfoCircleFilled />}
                                                type="text"
                                                onClick={() => setShowBatteryInfoTable(true)}
                                                style={{ fontSize: '22px', color: 'rgb(68 68 68 / 60%)' }}
                                            ></Button>
                                        </Tooltip>
                                    )}
                                </Form.Item>
                            </Col>
                        </Row>
                        <Row gutter={8} align="bottom">
                            <Col span={4}>
                                <Form.Item
                                    name={['reductionInfoMap', 'LIGHTING']}
                                    label={<Typography.Text strong>Lighting (kW)</Typography.Text>}
                                    rules={[createReductionInfoMethodValidator]}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={999999.999} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    name={['reductionInfoMap', 'WATER_HEATERS']}
                                    label={<Typography.Text strong>Water Heaters (kW)</Typography.Text>}
                                    rules={[createReductionInfoMethodValidator]}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={999999.999} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    name={['reductionInfoMap', 'REFRIGERATION']}
                                    label={<Typography.Text strong>Refrigeration (kW)</Typography.Text>}
                                    rules={[createReductionInfoMethodValidator]}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={999999.999} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    name={['reductionInfoMap', 'PLUG_LOAD']}
                                    label={<Typography.Text strong>Plug Load (kW)</Typography.Text>}
                                    rules={[createReductionInfoMethodValidator]}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={999999.999} />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    name={['reductionInfoMap', 'COMPUTING']}
                                    label={<Typography.Text strong>Computing (kW)</Typography.Text>}
                                    rules={[createReductionInfoMethodValidator]}
                                >
                                    <InputNumber style={{ width: '100px' }} min={0} max={999999.999} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Space direction="vertical" style={{ width: '100%' }} size={'large'}>
                            <Form.Item name={['generatorInfo']} noStyle>
                                {showGeneratorInfoTable && (
                                    <LocationGeneratorInfoTable
                                        key="location-generator-table"
                                        value={form.getFieldValue('generatorInfo') || []}
                                        onHide={() => setShowGeneratorInfoTable(false)}
                                        onChange={onGeneratorInfoChange}
                                    />
                                )}
                            </Form.Item>

                            <Form.Item name={['batteryInfo']} noStyle>
                                {showBatteryInfoTable && (
                                    <LocationBatteryInfoTable
                                        key="location-battery-table"
                                        value={form.getFieldValue('batteryInfo') || []}
                                        onHide={() => setShowBatteryInfoTable(false)}
                                        onChange={onBatteryInfoChange}
                                    />
                                )}
                            </Form.Item>
                        </Space>
                    </div>

                    {!isNew && (
                        <div className="form-section">
                            <Divider orientation="left" orientationMargin="0">
                                Registrations
                            </Divider>
                            <LocationRegistrationTable value={location.registrations} key="location-registration-table" />
                        </div>
                    )}
                </Form>
            </Modal>
        </div>
    );
};

function makeKey(label: string) {
    return `pjm-location-modal-${label}`;
}

function createUtilityResidentialCountValidator({ getFieldValue }: { getFieldValue: (name: NamePath<string>) => StoreValue }) {
    return {
        validator(rule: RuleObject, value: string) {
            if (value == null && getFieldValue(['siteInfo', 'utilityResidentialParticipant']) === true) {
                return Promise.reject(new Error('Please enter number of participants!'));
            }
            return Promise.resolve();
        },
    };
}

function createUtilityResidentialProgramTypeValidator({ getFieldValue }: { getFieldValue: (name: NamePath<string>) => StoreValue }) {
    return {
        validator(rule: RuleObject, value: string) {
            if (value == null && getFieldValue(['siteInfo', 'utilityResidentialParticipant']) === true) {
                return Promise.reject(new Error('Please select type!'));
            }
            return Promise.resolve();
        },
    };
}

function createEdcAccountNumberValidator(edcName?: string) {
    return ({ getFieldValue }: { getFieldValue: (name: NamePath<string>) => StoreValue }) => {
        return {
            validator: (rule: RuleObject, value: string) => {
                if (!value) return Promise.resolve();
                if (!edcName) return Promise.resolve();

                const result = locationEdcAccountNumber.isValid(value, edcName);
                if (result.valid) return Promise.resolve();

                return Promise.reject(result.error);
            },
        };
    };
}

function createMaxLoadValidator({ getFieldValue }: { getFieldValue: (name: NamePath<string>) => StoreValue }) {
    return {
        validator(_: RuleObject, value: number) {
            const loadReduction = getFieldValue(['profileInfo', 'loadReduction']);
            if (loadReduction && (value < loadReduction || !value)) {
                return Promise.reject(new Error('The Max Load value must be equal to or greater than the Maximum Load Reduction Capability'));
            }
            return Promise.resolve();
        },
    };
}

function createReductionInfoMethodValidator({ getFieldValue }: { getFieldValue: (name: NamePath<string>) => StoreValue }) {
    return {
        validator(_: RuleObject, value: StoreValue) {
            const reductionInfoMap = getFieldValue(['reductionInfoMap']);
            const allReductionMethodsAreZero =
                !reductionInfoMap['MANUFACTURING'] &&
                !reductionInfoMap['HVAC'] &&
                !reductionInfoMap['GENERATOR'] &&
                !reductionInfoMap['BATTERIES'] &&
                !reductionInfoMap['LIGHTING'] &&
                !reductionInfoMap['WATER_HEATERS'] &&
                !reductionInfoMap['REFRIGERATION'] &&
                !reductionInfoMap['PLUG_LOAD'] &&
                !reductionInfoMap['COMPUTING'];

            if (!value && allReductionMethodsAreZero) {
                return Promise.reject(new Error('At least one Reduction method must be set!'));
            }

            return Promise.resolve();
        },
    };
}

function calculateLoadReduction(reductionInfoMap?: Record<PjmLoadReductionType, number | null>) {
    const reductionList = Object.values(reductionInfoMap || {});

    const loadReduction = reductionList.reduce((prev: number, cur) => {
        prev = prev + (cur ?? 0);
        return prev;
    }, 0);

    return loadReduction;
}
